import { css } from 'aphrodite';
import * as React from 'react';
import { useKeepInTouchAllScores } from '../../../../queries/KeepInTouchAgent';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import {
	AppDrawer,
	AppDrawerSeparator,
	AppDrawerToolbar,
	AppDrawerToolbarButton,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../components-v2/Drawer/AppDrawer';
import { AssigneeIcon } from '../../../components/svgs/icons/AssigneeIcon';
import { DefaultUniversalAssistantChat } from '../../templates/AssistantChat';
import { ContactInfoAppDrawerContent } from '../PeopleContainer/AppDrawerContents/ContactInfo';
import { useContactsPageAppDrawerQueryParamState } from '../PeopleContainer/hooks/useContactsPageAppDrawerQueryParamState';
import { ConfigurationList } from './ConfigurationList';
import { ScoresView } from './ScoresView';
import { TopContactsView } from './TopContactsView';
import { styleSheet } from './styles';

type View = { type: 'default' } | { type: 'configs' } | { type: 'scores'; configId: string };

export const KeepInTouchAgentContainer: React.FC = () => {
	const [view, setView] = React.useState<View>({ type: 'default' });
	const { data: allScoresData, isLoading } = useKeepInTouchAllScores({ pageSize: 1, enabled: view.type === 'default' });
	const hasScores = (allScoresData?.pages?.[0]?.totalCount ?? 0) > 0;
	const firstContactId = allScoresData?.pages?.[0]?.values?.[0]?.contactId;
	const drawerState = useContactsPageAppDrawerQueryParamState();

	const handleViewScores = (configId: string) => {
		setView({ type: 'scores', configId });
	};

	const handleBack = () => {
		setView({ type: 'default' });
	};

	const renderContent = () => {
		if (view.type === 'scores') {
			return <ScoresView configId={view.configId} onBack={handleBack} />;
		}

		if (view.type === 'configs') {
			return <ConfigurationList onViewScores={handleViewScores} onBack={() => setView({ type: 'default' })} />;
		}

		if (isLoading) return <LoadingSpinner type='large' />;

		if (hasScores) {
			return <TopContactsView onManageJobs={() => setView({ type: 'configs' })} />;
		}

		return <ConfigurationList onViewScores={handleViewScores} />;
	};

	return (
		<>
			<div className={css(styleSheet.container)}>{renderContent()}</div>
			<AppDrawer
				value={drawerState.activeDrawer}
				onValueChange={value => {
					if (!value) {
						drawerState.clearDrawerValue();
						return;
					}
					if (value === 'contact-info') {
						const contactId = drawerState.drawerContactId ?? firstContactId;
						if (contactId) drawerState.setDrawerToContactInfo(contactId);
						return;
					}
					drawerState.setDrawerValue(value);
				}}
			>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<ContactInfoAppDrawerContent contactId={drawerState.drawerContactId} />
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
					<AppDrawerSeparator />
					<AppDrawerToolbarButton value='contact-info' tooltip='Contact preview'>
						<AssigneeIcon />
					</AppDrawerToolbarButton>
				</AppDrawerToolbar>
			</AppDrawer>
		</>
	);
};
